import { isAboveLevel } from '../logger/utils';
import { filterReporterMetadata } from '../utils';
export const reporters = {};
export let globalEventLevel;
/**
 * Sets the global event level.
 */
export function setEventLevel(level) {
    globalEventLevel = level ?? undefined;
}
/**
 * Gets reporters, optionally with filters.
 * @param filters Filters to remove or specify specific reporters.
 * @returns Selected reporters, or all registered reporters.
 */
function getReporters(filters) {
    let result = Object.entries(reporters);
    if (filters?.excludeReporters) {
        result = result.filter(([key]) => !filters?.excludeReporters?.includes(key));
    }
    if (filters?.toReporters) {
        result = result.filter(([key]) => filters?.toReporters?.includes(key));
    }
    return result.map(([, reporter]) => reporter);
}
/**
 * Tracks an Analytics event.
 */
export function trackEvent(event) {
    if (globalEventLevel && event.level && !isAboveLevel(event.level, globalEventLevel)) {
        return;
    }
    for (const reporter of getReporters(event)) {
        if (reporter.endpoints?.trackEvent === false) {
            continue;
        }
        else if (reporter.eventLevel && event.level && !isAboveLevel(event.level, reporter.eventLevel)) {
            return;
        }
        const reporterEvent = { ...event };
        reporterEvent.metadata = filterReporterMetadata(event.metadata, reporter);
        reporterEvent.metrics = filterReporterMetadata(event.metrics, reporter);
        reporterEvent.tags = filterReporterMetadata(event.tags, reporter);
        reporter.trackEvent(reporterEvent);
    }
}
/**
 * Tracks an Analytics event, recording the time since the last event, if available.
 */
export function trackEventSinceLastAction(event) {
    if (globalEventLevel && event.level && !isAboveLevel(event.level, globalEventLevel)) {
        return;
    }
    const lastEvent = getLastTrackedEvent();
    if (lastEvent) {
        const duration = new Date().getTime() - lastEvent.occurred.getTime();
        trackEvent({
            ...event,
            metadata: {
                ...event.metadata,
                lastEventName: lastEvent.message,
                timeSinceLastEvent: duration,
            },
        });
    }
    else {
        trackEvent(event);
    }
    sessionStorage.setItem('loggerLastEvent', JSON.stringify({ ...event, occurred: new Date() }));
}
/**
 * Gets the last tracked event, if available.
 */
export function getLastTrackedEvent() {
    const eventStr = sessionStorage.getItem('loggerLastEvent');
    if (!eventStr)
        return null;
    const event = JSON.parse(eventStr);
    event.occurred = new Date(event.occurred);
    return event;
}
/**
 * Breadcrumbs to create a trail of events that happened prior to an issue.
 * These events are very similar to traditional logs, but can record more rich structured data.
 */
export function addBreadcrumb(breadcrumb) {
    for (const reporter of getReporters(breadcrumb)) {
        if (reporter.endpoints?.addBreadcrumb === false) {
            continue;
        }
        else if (breadcrumb.category && reporter.ignoreBreadcrumbCategories?.includes(breadcrumb.category)) {
            continue;
        }
        const reporterBreadcrumb = { ...breadcrumb };
        reporterBreadcrumb.metadata = filterReporterMetadata(breadcrumb.metadata, reporter);
        reporter.addBreadcrumb(reporterBreadcrumb);
    }
}
/**
 * Adds global metadata to all events and logs.
 * @param metadata Metadata to add.
 * @param filters Optional filters to specify which reporters to add metadata to.
 */
export function addMetadata(metadata, filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.addMetadata === false) {
            continue;
        }
        reporter.addMetadata(filterReporterMetadata(metadata, reporter));
    }
}
/**
 * Sets the user information in Analytics.
 * @param user User information, or null to clear.
 * @param filters Optional filters to specify which reporters to set the user for.
 */
export function setUser(user, filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.setUser === false) {
            continue;
        }
        reporter.setUser(user);
    }
}
/**
 * Sets the route name in Analytics.
 * Some Analytics services use this differentiate SPA Route vs Page changes.
 * @param routeName Name of the Route.
 * @param filters Optional filters to specify which reporters to set the route name for.
 */
export function setRouteName(routeName, filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.setRouteName === false) {
            continue;
        }
        reporter.setRouteName(routeName);
    }
}
/**
 * Sets the page name in Analytics.
 * @param pageName Name of the Page.
 * @param filters Optional filters to specify which reporters to set the page name for.
 */
export function setPageName(pageName, filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.setPageName === false) {
            continue;
        }
        reporter.setPageName(pageName);
    }
}
/**
 * Reports an Error in Reporters that support error tracking.
 * @param error Error data.
 * @param metadata Metadata to add to the error.
 * @param filters Optional filters to specify which reporters to report the error to.
 */
export function reportError(error, metadata, filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.reportError === false) {
            continue;
        }
        reporter.reportError?.(error, filterReporterMetadata(metadata, reporter));
    }
}
/**
 * Report a feature flag evaluation, e.g. for surfacing in Datadog RUM.
 *
 * @param flag    Details about the feature flag evaluation
 * @param filters Filters to specify which reporters to start a session recording for.
 */
export function reportFeatureFlag(flag, filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.reportFeatureFlag === false) {
            continue;
        }
        reporter.reportFeatureFlag?.(flag);
    }
}
/**
 * Starts a session recording in Analytics, e.g. RUM Session Replay
 * @param filters Optional filters to specify which reporters to start a session recording for.
 */
export function recordSession(filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.recordSession === false) {
            continue;
        }
        reporter.recordSession?.();
    }
}
/**
 * Stops a session recording in Analytics, e.g. RUM Session Replay
 * @param filters Optional filters to specify which reporters to stop a session recording for.
 */
export function recordSessionStop(filters) {
    for (const reporter of getReporters(filters)) {
        if (reporter.endpoints?.recordSessionStop === false) {
            continue;
        }
        reporter.recordSessionStop?.();
    }
}
//# sourceMappingURL=index.js.map